import {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
} from 'axios';
import {
  type ReactNode,
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  ForbiddenActionError,
  type IFrontendUrls,
  type IRole,
  type IRoutePath,
  type IThrowsError,
  Logger,
  clearFeatureFlagsCache,
  formatUnknownErrorMessage,
  getRouteFrom,
  getStorage,
  prefetchFeatureFlags,
  removeStorage,
  setStorage,
  translateUserRoleInRole,
  useToast,
} from '@gbs-monorepo-packages/common';

import {
  AuthStorageKey,
  authBroadcastChannelEvents,
  authBroadcastChannelName,
} from '../constants';
import {
  AuthService,
  type IGetManagersProps,
  type IPaginationManagersDTO,
  createTermsService,
} from '../services';
import type * as Auth from '../services/auth';

export interface IRedirectOptions {
  to: string;
  replace?: boolean;
}

export interface ISignIn extends Auth.ISignInCredentialsDTO {
  redirect?: IRedirectOptions;
}

export interface IAuthContextData {
  forgotPassword: (credentials: Auth.IForgotCredentialsDTO) => Promise<void>;
  requestAccess: (credentials: Auth.IRequestAccessDTO) => Promise<void>;
  isAuthenticated: boolean;
  resetPassword: (
    credentials: Auth.IResetPasswordCredentialsDTO
  ) => Promise<void>;
  signIn: (credentials: ISignIn) => Promise<void>;
  syncUserData: (user: Auth.IUserDTO) => void;
  signOut: () => Promise<void>;
  user: Auth.IUserDTO | null;
  clientIdByUser: number;
  getCurrentRole: (userRoles: string[]) => IRole | null;
  saveUser: (
    user: Auth.IUserDTO,
    redirect?: IRedirectOptions | null,
    initialAccess?: boolean
  ) => void;
  refreshUserFromServer: () => Promise<boolean>;
  acceptTerms: (
    termsVersion: number,
    redirect?: IRedirectOptions
  ) => Promise<void>;
  getManagers: (
    { page, limit, filter, all }: IGetManagersProps,
    config?: AxiosRequestConfig
  ) => Promise<IPaginationManagersDTO>;
  latestTermsVersion: number;
  termsAcceptancePath: string | null;
  termsExemptPaths: string[];
  mainContentPath: string;
}

interface IAuthProps {
  authorizationRoute: IRoutePath;
  resetPasswordRoute: IRoutePath;
  homeRoute: IRoutePath;
  axiosInstance: AxiosInstance;
  children: ReactNode;
  customUrls?: Partial<Auth.IAuthUrls>;
  mainContentRoute: IRoutePath;
  frontendUrls?: Partial<IFrontendUrls>;
  termsAcceptanceRoute?: IRoutePath;
  termsDocumentRoute?: IRoutePath;
}

interface ILocation extends Location {
  state: { from?: string } | null;
}

export const AuthContext = createContext<IAuthContextData>(
  {} as IAuthContextData
);

const authChannel = new BroadcastChannel(authBroadcastChannelName);
const edgeRoute: string =
  import.meta.env.VITE_EDGE_URL ?? 'http://localhost:5173';
const adminRoute: string =
  import.meta.env.VITE_ADMIN_URL ?? 'http://localhost:5174';
const clientRoute: string =
  import.meta.env.VITE_CLIENT_URL ?? 'http://localhost:5175';

export const AuthProvider = ({
  authorizationRoute,
  resetPasswordRoute,
  homeRoute,
  axiosInstance,
  children,
  customUrls,
  mainContentRoute,
  frontendUrls = {
    edgeRoute,
    adminRoute,
    clientRoute,
  },
  termsAcceptanceRoute,
  termsDocumentRoute,
}: IAuthProps): JSX.Element => {
  const [user, setUser] = useState<Auth.IUserDTO | null>(
    getStorage<Auth.IUserDTO>(AuthStorageKey.USER)
  );
  const isAuthenticated = !!user;
  const navigate = useNavigate();
  const { pathname, state = null }: Partial<ILocation> = useLocation();

  const { addToast } = useToast();

  const clientIdByUser: number = useMemo(() => {
    return user?.clientId ?? 0;
  }, [user]);

  const [latestTermsVersion, setLatestTermsVersion] = useState<number>(() =>
    parseInt((import.meta.env.VITE_LATEST_TERMS_VERSION as string) ?? '1', 10)
  );

  const saveUser = useCallback(
    (
      user: Auth.IUserDTO,
      redirect?: IRedirectOptions | null,
      initialAccess?: boolean
    ) => {
      setUser(user);
      setStorage<Auth.IUserDTO>(AuthStorageKey.USER, user);

      const navigationState =
        initialAccess === true
          ? { ...state, initialAccess: true }
          : (state ?? null);

      if (redirect === undefined) {
        navigate(getRouteFrom(mainContentRoute), {
          replace: true,
          state: navigationState,
        });
      } else if (redirect !== null) {
        let { to } = redirect;
        const { replace } = redirect;

        const fromResetPasswordRoute =
          to === getRouteFrom(resetPasswordRoute) &&
          pathname === getRouteFrom(authorizationRoute);
        const fromHomeRoute =
          to === getRouteFrom(homeRoute) &&
          pathname === getRouteFrom(authorizationRoute);

        if (to === pathname || fromResetPasswordRoute || fromHomeRoute) {
          to = getRouteFrom(mainContentRoute);
        }

        navigate(to, {
          replace,
          state: navigationState,
        });
      }
    },
    [
      mainContentRoute,
      navigate,
      state,
      resetPasswordRoute,
      authorizationRoute,
      homeRoute,
      pathname,
    ]
  );

  const authService = useMemo(
    () => new AuthService(axiosInstance, customUrls, frontendUrls),
    [axiosInstance, customUrls, frontendUrls]
  );

  const refreshUserFromServer = useCallback(async (): Promise<boolean> => {
    if (!localStorage.getItem('JWT_TOKEN')) {
      return false;
    }
    try {
      const fresh = await authService.refresh();
      const stored = getStorage<Auth.IUserDTO>(AuthStorageKey.USER);
      const merged: Auth.IUserDTO = stored
        ? {
            ...stored,
            ...fresh,
            isMainManager:
              fresh.isMainManager !== undefined
                ? fresh.isMainManager
                : stored.isMainManager,
          }
        : fresh;
      saveUser(merged, null);
      return true;
    } catch (err: unknown) {
      Logger.debug('refreshUserFromServer: ', err);
      addToast({
        title: 'Could not refresh account',
        description: formatUnknownErrorMessage(err),
        styleType: 'error',
        dataCy: 'refresh-user-error-toast',
      });
      return false;
    }
  }, [authService, saveUser, addToast]);

  const termsService = useMemo(
    () => createTermsService(axiosInstance),
    [axiosInstance]
  );

  const removeUser = useCallback(
    (clearStateFrom = false) => {
      setUser(null);
      removeStorage(AuthStorageKey.USER);
      clearFeatureFlagsCache();

      const navigationState = clearStateFrom ? null : { from: pathname };

      navigate(getRouteFrom(authorizationRoute), {
        replace: true,
        state: navigationState,
      });
    },
    [navigate, authorizationRoute, pathname]
  );

  const forgotPassword = useCallback(
    async ({ email }: Auth.IForgotCredentialsDTO) => {
      await authService.forgotPassword({ email }).catch((err: IThrowsError) => {
        throw err;
      });
    },
    [authService]
  );

  const requestAccess = useCallback(
    async (credentials: Auth.IRequestAccessDTO) => {
      await authService
        .requestAccess(credentials)
        .catch((err: IThrowsError) => {
          throw err;
        });
    },
    [authService]
  );

  const resetPassword = useCallback(
    async ({ email, password }: Auth.IResetPasswordCredentialsDTO) => {
      await authService
        .resetPassword({ email, password })
        .catch((err: IThrowsError) => {
          throw err;
        });
    },
    [authService]
  );

  const termsAcceptancePath: string | null = useMemo(
    () => (termsAcceptanceRoute ? getRouteFrom(termsAcceptanceRoute) : null),
    [termsAcceptanceRoute]
  );

  const termsExemptPaths: string[] = useMemo(() => {
    const paths: string[] = [];
    if (termsAcceptancePath) paths.push(termsAcceptancePath);
    if (termsDocumentRoute) paths.push(getRouteFrom(termsDocumentRoute));
    return paths;
  }, [termsAcceptancePath, termsDocumentRoute]);

  const mainContentPath = useMemo(
    () => getRouteFrom(mainContentRoute),
    [mainContentRoute]
  );

  const signIn = useCallback(
    async ({ email, password, redirect }: ISignIn) => {
      const { data: user, token } = await authService
        .signIn({ email, password })
        .catch((err: AxiosError<Auth.IResponseWithCode>) => {
          throw err;
        });

      if (window.location.origin === authService.getEdgeRoute()) {
        localStorage.setItem('JWT_TOKEN', token);
        saveUser(user, null, false);
      }

      if (
        redirect === undefined ||
        !(window.location.origin === authService.getEdgeRoute())
      ) {
        const redirectPortal = authService.getRedirectPortal(user, token);
        if (redirectPortal) {
          window.location.assign(redirectPortal);
          return;
        }
      }

      localStorage.setItem('JWT_TOKEN', token);
      saveUser(user, redirect, true);
    },
    [authService, saveUser]
  );

  const syncUserData = useCallback(
    (user: Auth.IUserDTO) => {
      saveUser(user, null);
    },
    [saveUser]
  );

  const acceptTerms = useCallback(
    async (termsVersion: number, redirect?: IRedirectOptions) => {
      await termsService.acceptTerms({ termsVersion }).catch((err: unknown) => {
        throw err;
      });

      if (user) {
        const updatedUser: Auth.IUserDTO = {
          ...user,
          currentTermsVersionAccepted: termsVersion,
        };

        const token = localStorage.getItem('JWT_TOKEN') ?? '';
        const redirectPortal = authService.getRedirectPortal(
          updatedUser,
          token
        );
        if (redirectPortal) {
          window.location.assign(redirectPortal);
          return;
        }

        saveUser(updatedUser, redirect ?? undefined);
      }
    },
    [termsService, user, saveUser, authService]
  );

  const signOut = useCallback(async () => {
    try {
      await authService.signOut();

      authChannel.postMessage(authBroadcastChannelEvents.SignOut);
      removeUser(true);
    } catch (err) {
      Logger.debug('err:  ', err);
    }
  }, [authService, removeUser]);

  const getManagers = useCallback(
    async (
      { page, limit, filter, all = false }: IGetManagersProps,
      config?: AxiosRequestConfig
    ): Promise<IPaginationManagersDTO> => {
      return await authService.getManagers(
        { page, limit, filter, all },
        config
      );
    },
    [authService]
  );

  const getCurrentRole = (userRoles: string[]): IRole | null => {
    let currentRole = null;
    for (const value of userRoles.values()) {
      const userRoleAux = translateUserRoleInRole[value] ?? null;
      // userRoleAux can be null
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      if (!userRoleAux) {
        continue;
      }

      if (!currentRole) {
        currentRole = userRoleAux;
      } else if (currentRole.level < userRoleAux.level) {
        currentRole = userRoleAux;
      }
    }

    return currentRole;
  };

  const userId = user?.id ?? null;

  useEffect(() => {
    if (userId === null) return;

    prefetchFeatureFlags(axiosInstance);

    const fetchVersion = () => {
      termsService
        .getLatestVersion()
        .then(({ version }) => {
          setLatestTermsVersion(version);
        })
        .catch(() => {
          // keep current value on failure
        });
    };

    fetchVersion();
    window.addEventListener('focus', fetchVersion);
    return () => window.removeEventListener('focus', fetchVersion);
  }, [userId, termsService, axiosInstance]);

  useEffect(() => {
    if (!user) return;
    const storedUser = getStorage<Auth.IUserDTO>(AuthStorageKey.USER);
    if (
      storedUser !== null &&
      (storedUser.currentTermsVersionAccepted ?? 0) !==
        (user.currentTermsVersionAccepted ?? 0)
    ) {
      setUser(storedUser);
    }
  }, [pathname]);

  useEffect(() => {
    authChannel.onmessage = (message: { data?: string }) => {
      switch (message.data) {
        case authBroadcastChannelEvents.SignOut:
          removeUser();
          break;
        case authBroadcastChannelEvents.NotifyForbidden:
          addToast({
            title: 'Action not allowed',
            description: ForbiddenActionError,
            styleType: 'error',
            dataCy: 'forbidden-error-toast',
          });
          break;
        default:
          break;
      }
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        forgotPassword,
        requestAccess,
        isAuthenticated,
        resetPassword,
        signIn,
        syncUserData,
        signOut,
        user,
        clientIdByUser,
        getCurrentRole,
        saveUser,
        refreshUserFromServer,
        acceptTerms,
        getManagers,
        latestTermsVersion,
        termsAcceptancePath,
        termsExemptPaths,
        mainContentPath,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
